import type { ReactNode } from "react";

import { Box, Checkbox, Field, Flex, useCheckbox } from "@chakra-ui/react";

type Props = {
  disabled?: boolean;
  errorMessage?: string;
  label: ReactNode;
  value: "indeterminate" | boolean;
  isInvalid?: boolean;
  isRequired?: boolean;
  onChange: (checked: "indeterminate" | boolean) => void;
};

export const CheckboxField = ({
  disabled,
  errorMessage,
  label,
  value,
  isInvalid,
  isRequired,
  onChange,
}: Props) => {
  return (
    <Field.Root
      disabled={disabled}
      invalid={Boolean(isInvalid)}
      required={isRequired}
    >
      <CheckboxFieldControl label={label} value={value} onChange={onChange} />
      {isInvalid && <Field.ErrorText>{errorMessage} &nbsp;</Field.ErrorText>}
    </Field.Root>
  );
};

type ControlProps = Pick<Props, "label" | "onChange" | "value">;

const CheckboxFieldControl = ({ label, value, onChange }: ControlProps) => {
  const checkbox = useCheckbox({
    checked: value,
    onCheckedChange: ({ checked }) => onChange(checked),
  });

  return (
    <Flex alignItems="center" gap="2.5">
      <Checkbox.RootProvider value={checkbox}>
        <Checkbox.HiddenInput />
        <Checkbox.Control
          _checked={{
            bg: "bg.accent",
            borderColor: "bg.accent",
          }}
          borderRadius="md"
        />
      </Checkbox.RootProvider>
      <Box
        {...checkbox.getLabelProps()}
        _disabled={{ opacity: "0.5" }}
        fontWeight="medium"
        userSelect="none"
      >
        {label}
      </Box>
    </Flex>
  );
};
